import { createSlice } from "@reduxjs/toolkit";

const WORKSPACE_STORAGE_KEY = "cdt:selectedWorkspace";

const readStoredWorkspaceKey = () => {
  if (typeof window === "undefined") return "personal";

  try {
    return window.localStorage.getItem(WORKSPACE_STORAGE_KEY) || "personal";
  } catch {
    return "personal";
  }
};

export const persistWorkspaceKey = (workspaceKey) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(WORKSPACE_STORAGE_KEY, workspaceKey || "personal");
  } catch {
    // Ignore storage failures.
  }
};

export const buildWorkspaceOptions = ({ user, organizations = [] }) => {
  const personalWorkspace = {
    key: "personal",
    type: "personal",
    name: user?.firstName ? `${user.firstName}'s Workspace` : "Personal workspace",
    description: "Your personal projects and demo access",
  };

  const organizationWorkspaces = Array.isArray(organizations)
    ? organizations.map((organization) => ({
        key: `org:${organization._id}`,
        type: "organization",
        id: organization._id,
        name: organization.name,
        slug: organization.slug,
        role: organization.membership?.orgRole || "member",
        description: `Organization workspace${organization.membership?.orgRole ? ` · ${organization.membership.orgRole}` : ""}`,
      }))
    : [];

  return [personalWorkspace, ...organizationWorkspaces];
};

export const resolveCurrentWorkspace = ({
  selectedWorkspaceKey,
  user,
  organizations = [],
}) => {
  const options = buildWorkspaceOptions({ user, organizations });
  return options.find((workspace) => workspace.key === selectedWorkspaceKey) || options[0];
};

const initialState = {
  organizations: [],
  selectedWorkspaceKey: readStoredWorkspaceKey(),
  loading: false,
  error: null,
};

const workspaceSlice = createSlice({
  name: "workspace",
  initialState,
  reducers: {
    fetchWorkspacesStart(state) {
      state.loading = true;
      state.error = null;
    },
    fetchWorkspacesSuccess(state, { payload }) {
      state.loading = false;
      state.organizations = Array.isArray(payload) ? payload : [];
      state.error = null;
    },
    fetchWorkspacesFailure(state, { payload }) {
      state.loading = false;
      state.error = payload || null;
    },
    setSelectedWorkspaceKey(state, { payload }) {
      state.selectedWorkspaceKey = payload || "personal";
    },
    clearWorkspaceState(state) {
      state.organizations = [];
      state.selectedWorkspaceKey = "personal";
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  fetchWorkspacesStart,
  fetchWorkspacesSuccess,
  fetchWorkspacesFailure,
  setSelectedWorkspaceKey,
  clearWorkspaceState,
} = workspaceSlice.actions;

export const workspaceReducer = workspaceSlice.reducer;